/**
 * TKSearch — shared search-to-sell module (Rebuilt for Shopify Products).
 * Contract: .t/arch/tk-search/constraints.yaml (TKS-01..TKS-07).
 * Emits: 'tk-search-pick' { detail: { device, source } }, 'tk-search-open', 'tk-search-close'.
 */
(function () {
  "use strict";

  const DEBOUNCE_MS = 150;
  const BLUR_GRACE_MS = 200;

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }
  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function fmtMoneyCents(cents) {
    if (window.TKBuyback && typeof TKBuyback.fmtMoney === 'function') return TKBuyback.fmtMoney(cents);
    try { return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(cents / 100); }
    catch (e) { return '$' + Math.round(cents / 100).toLocaleString(); }
  }
  function parsePrice(s) {
    if (window.TKBuyback && typeof TKBuyback.parsePrice === 'function') return TKBuyback.parsePrice(s);
    const n = parseFloat(String(s || '').replace(/,/g, ''));
    return isFinite(n) ? Math.round(n * 100) : 0;
  }

  // The search API returns the variant price, which RMS does not keep current; the
  // "up to" amount lives in the catalog RMS pushes hourly, keyed by product handle.
  let uptoPromise = null;
  function loadUpto() {
    if (uptoPromise) return uptoPromise;
    uptoPromise = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem('tk-upto') || 'null');
        if (cached && Date.now() - cached.at < 600000) return new Map(cached.rows);
      } catch (e) {}
      const r = await fetch('/?view=catalog-json', { headers: { Accept: 'application/json' } });
      const d = await r.json();
      const rows = [];
      ['phones', 'tablets', 'mac', 'consoles'].forEach((k) => (d[k] || []).forEach((m) => {
        const cents = parsePrice(m.upto);
        if (m.slug && cents > 0) rows.push([m.slug, cents]);
      }));
      try { sessionStorage.setItem('tk-upto', JSON.stringify({ at: Date.now(), rows })); } catch (e) {}
      return new Map(rows);
    })().catch(() => { uptoPromise = null; return new Map(); });
    return uptoPromise;
  }
  window.TKUpto = { load: loadUpto, fmt: fmtMoneyCents };

  function renderListItems(items, activeIdx, listboxId) {
    if (items.length === 0) {
      return '<li class="tk-search__empty" role="status">No matches. Try another model.</li>';
    }
    return items.map((p, i) => {
      const id = listboxId + '-opt-' + i;
      const active = i === activeIdx;
      
      let imgUrl = '';
      if (p.featured_image) {
        imgUrl = p.featured_image.url || p.featured_image;
      } else if (p.image) {
        imgUrl = p.image;
      }
      const img = imgUrl ? `<img src="${escHtml(imgUrl)}" alt="" width="40" height="40" loading="lazy" decoding="async">` : '';
      
      const cents = p.uptoCents || 0;
      const upto = cents > 0 ? `<span class="tk-search__list-price">Up to <strong>${fmtMoneyCents(cents)}</strong></span>` : '';
      
      // Fallback to vendor if type isn't returned by API. We'll uppercase it to match the visual style.
      const catText = (p.type || p.vendor || '').toUpperCase();
      const cat = catText ? `<span class="tk-search__list-cat">${escHtml(catText)}</span>` : '';
      
      return `<li id="${id}" class="tk-search__opt${active ? ' is-active' : ''}" role="option" aria-selected="${active}" data-tk-search-opt data-tk-search-url="${escHtml(p.url)}">
        <span class="tk-search__list-thumb">${img}</span>
        <span class="tk-search__list-info">
          <span class="tk-search__list-name">${escHtml(p.title)}</span>
          <span class="tk-search__list-meta">${cat}${upto}</span>
        </span>
        <span class="tk-search__list-arrow" aria-hidden="true">→</span>
      </li>`;
    }).join('');
  }

  function init(root) {
    if (!root || root.dataset.tkSearchInit === '1') return;
    root.dataset.tkSearchInit = '1';

    const variant = root.getAttribute('data-tk-search') || 'finder';
    const onPick = root.getAttribute('data-tk-search-on-pick') || 'navigate';
    const maxResults = parseInt(root.getAttribute('data-tk-search-max') || '8', 10);
    const field = $('.tk-search__field', root);
    const input = $('[data-tk-search-input]', root);
    const listEl = $('[data-tk-search-list]', root);
    const clearBtn = $('[data-tk-search-clear]', root);
    const submitBtn = $('[data-tk-search-submit]', root);
    const spinner = $('[data-tk-search-spinner]', root);
    const listboxId = listEl.id;
    if (!input || !listEl) return;

    let items = [];
    let activeIdx = -1;
    let blurTimer = null;
    let debounceTimer = null;
    let abortController = null;

    function emit(name, detail) {
      try { window.dispatchEvent(new CustomEvent(name, { detail: detail || {} })); } catch (e) {}
    }

    function open() {
      if (!listEl.hidden) return;
      listEl.hidden = false;
      field.setAttribute('aria-expanded', 'true');
      emit('tk-search-open', { source: variant });
    }
    function close() {
      if (listEl.hidden) return;
      listEl.hidden = true;
      field.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIdx = -1;
      emit('tk-search-close', { source: variant });
    }

    async function update(q) {
      const rawQuery = (q || '').trim();
      let defaultQuery = 'iphone';
      const cat = (root.getAttribute('data-tk-search-category') || '').toLowerCase();
      if (cat.includes('tablet')) defaultQuery = 'ipad';
      else if (cat.includes('mac') || cat.includes('laptop')) defaultQuery = 'macbook';
      else if (cat.includes('game') || cat.includes('console')) defaultQuery = 'playstation';
      else if (cat.includes('watch')) defaultQuery = 'apple watch';

      const query = rawQuery === '' ? defaultQuery : rawQuery;

      if (abortController) {
        abortController.abort();
      }
      abortController = new AbortController();
      const signal = abortController.signal;

      if (spinner) spinner.hidden = false;

      try {
        const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${maxResults}`, { signal });
        if (!res.ok) throw new Error('Network response was not ok');
        const [data, upto] = await Promise.all([res.json(), loadUpto()]);
        items = (data.resources?.results?.products || []).map((p) => ({ ...p, uptoCents: upto.get(p.handle) || 0 }));
        
        activeIdx = items.length > 0 ? 0 : -1;
        listEl.innerHTML = renderListItems(items, activeIdx, listboxId);
        if (activeIdx >= 0) input.setAttribute('aria-activedescendant', listboxId + '-opt-' + activeIdx);
        else input.removeAttribute('aria-activedescendant');
        if (spinner) spinner.hidden = true;
        open();
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Search suggestion error:', err);
        items = [];
        listEl.innerHTML = '<li class="tk-search__empty" role="status">Search failed. Try again.</li>';
        if (spinner) spinner.hidden = true;
        open();
      }
    }

    function pick(idx) {
      if (idx < 0 || idx >= items.length) return;
      const product = items[idx];
      close();
      emit('tk-search-pick', { device: product, source: variant });
      if (onPick === 'navigate' && product.url) {
        location.assign(product.url);
      }
    }

    function setActive(idx) {
      if (items.length === 0) return;
      activeIdx = ((idx % items.length) + items.length) % items.length;
      $$('[data-tk-search-opt]', listEl).forEach((el, i) => {
        const on = i === activeIdx;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      input.setAttribute('aria-activedescendant', listboxId + '-opt-' + activeIdx);
      const el = listEl.children[activeIdx];
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }

    input.addEventListener('focus', () => {
      if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
      update(input.value);
    });
    input.addEventListener('input', () => {
      clearBtn.hidden = !input.value;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => update(input.value), DEBOUNCE_MS);
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); if (!listEl.hidden && items.length) setActive(activeIdx + 1); else update(input.value); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); if (!listEl.hidden && items.length) setActive(activeIdx - 1); }
      else if (e.key === 'Enter') { e.preventDefault(); pick(activeIdx); }
      else if (e.key === 'Escape') { e.preventDefault(); close(); input.blur(); }
    });
    input.addEventListener('blur', () => {
      blurTimer = setTimeout(close, BLUR_GRACE_MS);
    });

    listEl.addEventListener('mousedown', (e) => {
      // Prevent input blur before click handler fires.
      e.preventDefault();
    });
    listEl.addEventListener('click', (e) => {
      const opt = e.target.closest('[data-tk-search-opt]');
      if (!opt) return;
      const idx = $$('[data-tk-search-opt]', listEl).indexOf(opt);
      pick(idx);
    });
    clearBtn.addEventListener('click', () => {
      input.value = '';
      clearBtn.hidden = true;
      input.focus();
      update('');
    });
    if (submitBtn) {
      // Keep input focus on press, then pick the top match (or load then pick).
      submitBtn.addEventListener('mousedown', (e) => e.preventDefault());
      submitBtn.addEventListener('click', () => {
        if (items.length > 0) { pick(activeIdx >= 0 ? activeIdx : 0); return; }
        const p = update(input.value);
        if (p && typeof p.then === 'function') p.then(() => { if (items.length) pick(0); });
      });
    }

    if (input.hasAttribute('data-tk-search-autofocus')) {
      setTimeout(() => input.focus(), 50);
    }
  }

  function bindAll() {
    $$('[data-tk-search]').forEach(init);
  }

  function render(opts) {
    opts = opts || {};
    const variant = opts.variant || 'finder';
    const placeholder = opts.placeholder || "Search to sell — try 'iPhone 16' or 'MacBook'";
    const onPick = opts.on_pick || 'navigate';
    const categoryFilter = opts.category_filter || '';
    const maxResults = opts.max_results || 8;
    const listboxId = opts.listbox_id || ('tk-search-list-' + variant + '-' + Math.random().toString(36).slice(2, 8));
    const autofocus = opts.auto_focus ? 'data-tk-search-autofocus' : '';
    const submitBtn = opts.show_submit
      ? `<button type="button" class="tk-search__submit" data-tk-search-submit aria-label="Search devices"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg><span class="tk-search__submit-label">${escHtml(opts.submit_label || 'Search')}</span></button>`
      : '';
    return `
    <div class="tk-search tk-search--${escHtml(variant)}" data-tk-search="${escHtml(variant)}" data-tk-search-on-pick="${escHtml(onPick)}" data-tk-search-category="${escHtml(categoryFilter)}" data-tk-search-max="${maxResults}">
      <div class="tk-search__field" role="combobox" aria-haspopup="listbox" aria-expanded="false" aria-owns="${listboxId}">
        <span class="tk-search__icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
        </span>
        <input type="search" inputmode="search" class="tk-search__input" placeholder="${escHtml(placeholder)}" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" aria-label="${escHtml(placeholder)}" aria-controls="${listboxId}" aria-autocomplete="list" data-tk-search-input ${autofocus}>
        <button type="button" class="tk-search__clear" data-tk-search-clear aria-label="Clear search" hidden>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/></svg>
        </button>
        <span class="tk-search__spinner" data-tk-search-spinner aria-hidden="true" hidden></span>
        ${submitBtn}
      </div>
      <ul class="tk-search__list" id="${listboxId}" role="listbox" aria-label="Suggested devices" data-tk-search-list hidden></ul>
    </div>`;
  }

  window.TKSearch = { init, bindAll, render };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }
})();
